import { useLayoutEffect, useState } from 'react';
import type { InlinePanelHeightOptions } from '../types/inline-panel-height.types';

/**
 * Fits an inline panel into its consumer's remaining space, without keyboard
 * heuristics, global scroll changes or measuring a hidden editor.
 * @param options - Visible panel, preferred height and shared layout budget.
 * @returns The available panel height in CSS pixels.
 */
export const useInlinePanelHeight = ({
  enabled,
  panelRef,
  heightBoundary,
  preferredHeight,
  occludedHeight = 0,
}: InlinePanelHeightOptions) => {
  const [height, setHeight] = useState(preferredHeight);
  useLayoutEffect(() => {
    if (!enabled) return;
    const panel = panelRef.current;
    const container = heightBoundary?.containerRef.current;
    const flexible = heightBoundary?.flexibleRef.current;
    if (!panel || !container || !flexible) {
      setHeight(preferredHeight);
      return;
    }
    const measure = () => {
      const overflow = Math.max(
        0,
        container.scrollHeight - container.clientHeight,
        // Integer scroll/client heights can hide fractional clipping at the
        // viewport edge, especially when a full-width dock has no bottom pad.
        // A sibling dock reduces the content boundary's height; its own box
        // is outside that boundary by design, not overflowing content.
        container.contains(panel)
          ? panel.getBoundingClientRect().bottom -
              container.getBoundingClientRect().bottom
          : 0
      );
      const flexibleBoxHeight = flexible.getBoundingClientRect().height;
      const flexibleHeight =
        flexibleBoxHeight > 0
          ? Math.max(
              0,
              flexibleBoxHeight -
                (flexible.offsetHeight - flexible.clientHeight)
            )
          : flexible.clientHeight;
      const available =
        (panel.getBoundingClientRect().height || panel.offsetHeight) +
        occludedHeight +
        flexibleHeight -
        heightBoundary.minimumFlexibleHeight -
        overflow;
      // Keep the same fractional budget as the auto-growing editor, including
      // when the native viewport becomes an equally sized inline replacement.
      setHeight(Math.max(0, Math.min(preferredHeight, available)));
    };
    measure();
    const observer =
      typeof ResizeObserver === 'undefined'
        ? undefined
        : new ResizeObserver(measure);
    [panel, container, flexible].forEach((element) =>
      observer?.observe(element)
    );
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [enabled, panelRef, heightBoundary, preferredHeight, occludedHeight]);
  return height;
};
